/**
 * Ad tags (GitHub #279) — author the LiveIntent HTML that fills a
 * template's declared ad slots, stored on the recurring series so one fill
 * applies to every edition (`AdSlotFill.binding.kind === "series"`).
 *
 * WHAT THIS SCREEN DOES AND DOES NOT DO — read before changing the copy below.
 *
 * Authoring is real and complete: `POST /series` stores the fills, `GET
 * /orgs/:org/series` reads them back, the domain stamps each fill's binding to
 * this series and refuses a slot filled twice, and the round-trip is covered
 * (packages/domain/test/series.test.ts, packages/integration-tests/test/series-route.test.ts).
 *
 * Placement is the legacy one: the series' Compose settings name which fill
 * goes at `{{MARQUEEAD}}`, at `{{SAFERTB}}` and after each main story
 * (`marqueeSlot`, `footerSlot`, `bodySlots`), so the editors listed here are
 * those slots, read from the series' stored content. Ad HTML is inserted
 * verbatim and never tracked, as required for advertiser markup.
 */
import { useEffect, useMemo, useState } from "react";
import { api, type CampaignSeries, type NewsletterPolicy, type SaveSeriesBody, type Template } from "../api.js";
import { useAsync } from "../useAsync.js";
import { AutoOpensNote } from "../AutoOpensNote.js";
import { SkeletonTable } from "../Skeleton.js";

export function AdTags({ org }: { org: string }) {
  const series = useAsync(() => api.series(org), [org]);
  const templates = useAsync(() => api.templates(org), [org]);
  const [selectedId, setSelectedId] = useState("");
  const [reportId, setReportId] = useState("");
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [localSeries, setLocalSeries] = useState<CampaignSeries[] | undefined>();
  const [form, setForm] = useState<SaveSeriesBody>({ orgId: org, seriesId: "", name: "", cadence: "weekly", templateId: "", adSlotFills: [] });

  const rows = localSeries ?? series.data ?? [];
  const seriesReport = useAsync(
    () => reportId ? api.seriesReport(org, reportId) : Promise.resolve(null),
    [org, reportId],
  );
  const selected = rows.find((item) => item.seriesId === selectedId);
  const template = (templates.data ?? []).find((item) => item.templateId === form.templateId);
  // The series' stored content carries the slots its editions actually place.
  // A series made in Compose has no template at all, so the template's own list
  // (the old source) left it with no editors and no way to save.
  const content = useAsync(
    () => editing && selectedId ? api.campaignContent(org, selectedId).catch(() => null) : Promise.resolve(null),
    [org, selectedId, editing],
  );
  const placed = useMemo(() => placedSlots(content.data?.newsletter), [content.data]);
  const composed = !!content.data && form.templateId === "";
  const slots = useMemo(
    () => content.data?.newsletter ? placed.map((p) => p.slot) : template?.adSlots ?? [...new Set(form.adSlotFills.map((fill) => fill.slot))],
    [content.data, placed, template, form.adSlotFills],
  );
  const labelFor = (slot: string) => placed.find((p) => p.slot === slot)?.label;
  // A fill whose slot the template has since stopped declaring gets no editor
  // row, so it is invisible and unremovable here while still being listed as a
  // chip on the series card. Naming them — and dropping them on save below, so
  // the save matches what the editor showed — is the honest resolution; keeping
  // them would re-save a fill the operator cannot see, read or delete.
  const orphans = useMemo(
    () => form.adSlotFills.map((fill) => fill.slot).filter((slot) => !slots.includes(slot)),
    [form.adSlotFills, slots],
  );

  useEffect(() => {
    if (!selected) return;
    setForm({ orgId: org, seriesId: selected.seriesId, name: selected.name, cadence: selected.cadence, templateId: selected.templateId, adSlotFills: selected.adSlotFills.map((fill) => ({ slot: fill.slot, html: fill.html, version: fill.version })) });
  }, [org, selected]);

  const begin = (item?: CampaignSeries) => {
    setMessage("");
    if (item) {
      setSelectedId(item.seriesId);
      setForm({ orgId: org, seriesId: item.seriesId, name: item.name, cadence: item.cadence, templateId: item.templateId, adSlotFills: item.adSlotFills.map((fill) => ({ slot: fill.slot, html: fill.html, version: fill.version })) });
    } else {
      const first = templates.data?.[0];
      setSelectedId("");
      setForm({ orgId: org, seriesId: "", name: "", cadence: "weekly", templateId: first?.templateId ?? "", adSlotFills: [] });
    }
    setEditing(true);
  };

  const save = async () => {
    setMessage("");
    if (!form.seriesId || !form.name || (!form.templateId && !composed)) return setMessage("Series id, name and template are required.");
    // Only slots the template declares, and only those with content: an orphan
    // fill has no editor row, so re-saving one would persist HTML the operator
    // was never shown and cannot remove.
    const fills = form.adSlotFills
      .filter((fill) => slots.includes(fill.slot) && fill.html.trim())
      .map((fill) => ({ ...fill, html: fill.html, version: fill.version ?? 1 }));
    try {
      const saved = await api.saveSeries({ ...form, seriesId: form.seriesId.trim(), name: form.name.trim(), adSlotFills: fills });
      setSelectedId(saved.seriesId);
      setEditing(false);
      setMessage("Ad tags saved.");
      setLocalSeries([...rows.filter((item) => item.seriesId !== saved.seriesId), saved]);
    } catch (e) { setMessage((e as Error).message); }
  };

  const fillFor = (slot: string) => form.adSlotFills.find((fill) => fill.slot === slot)?.html ?? "";
  const setFill = (slot: string, html: string) => setForm((current) => ({ ...current, adSlotFills: [...current.adSlotFills.filter((fill) => fill.slot !== slot), { slot, html, version: current.adSlotFills.find((fill) => fill.slot === slot)?.version ?? 1 }] }));

  return <div>
    <div className="pagehead"><div><h1>Ad tags</h1><p>LiveIntent ad HTML, one fill per slot — held on the series rather than on a single campaign.</p></div><button className="btn" onClick={() => begin()}>＋ Add series</button></div>
    <div className="card" role="status" style={{ borderLeft: "3px solid #2d7a46" }}>
      <strong>Series fills are active.</strong>
      <p className="muted" style={{ marginBottom: 0 }}>Each newsletter's Compose &amp; schedule settings say which slot goes at the template's <code>{"{{MARQUEEAD}}"}</code> (top), <code>{"{{SAFERTB}}"}</code> (footer) and after each main story. Paste each slot's ad HTML here; it is inserted verbatim and never tokenized or click-tracked.</p>
    </div>
    {message && <p className="muted">{message}</p>}
    {reportId && seriesReport.loading && <SkeletonTable rows={3} label="Loading series report…" />}
    {reportId && seriesReport.error && <p className="err">Could not load series report: {seriesReport.error}</p>}
    {reportId && seriesReport.data && <div className="card">
      <div className="cardhead" style={{ margin: "-18px -18px 16px" }}><h2>Series reporting · {reportId}</h2></div>
      <div className="kpi-grid">
        <div><span className="muted">Sent</span><strong>{seriesReport.data.aggregate.sent}</strong></div>
        <div><span className="muted">Delivered</span><strong>{seriesReport.data.aggregate.delivered}</strong></div>
        <div><span className="muted">Unique opens</span><strong>{seriesReport.data.aggregate.opens}</strong></div>
        <div><span className="muted">Unique clicks</span><strong>{seriesReport.data.aggregate.clicks}</strong></div>
      </div>
      <p className="muted">{seriesReport.data.editions.length} edition{seriesReport.data.editions.length === 1 ? "" : "s"} included. Counts are summed across editions.</p>
      <AutoOpensNote />
      <button className="btn ghost" onClick={() => setReportId("")}>Close report</button>
    </div>}
    {editing && <div className="card">
      <div className="cardhead" style={{ margin: "-18px -18px 16px" }}><h2>{selected ? "Edit series ad tags" : "Create series ad tags"}</h2></div>
      <div style={{ display: "flex", gap: 12 }}><label style={{ flex: 1 }}>Series id<input value={form.seriesId} disabled={!!selected} onChange={(e) => setForm({ ...form, seriesId: e.target.value })} placeholder="weekly-news" /></label><label style={{ flex: 2 }}>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Weekly news" /></label></div>
      <div style={{ display: "flex", gap: 12 }}>{composed
        ? <p className="muted" style={{ flex: 1 }}>Built from the HTML saved with this newsletter in Compose &amp; schedule.</p>
        : <label style={{ flex: 1 }}>Template<select value={form.templateId} onChange={(e) => setForm({ ...form, templateId: e.target.value, adSlotFills: [] })}>{(templates.data ?? []).map((item: Template) => <option key={item.templateId} value={item.templateId}>{item.name} ({item.templateId})</option>)}</select></label>}{/* A Compose-made series' timing is its cron, on Schedules; this field would mean nothing. */}
        {!composed && <label style={{ flex: 1 }}>Cadence<select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value as SaveSeriesBody["cadence"] })}><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="biweekly">Biweekly</option><option value="monthly">Monthly</option></select></label>}</div>
      {content.loading && editing && selectedId ? <p className="muted">Loading this newsletter's ad slots…</p>
        : slots.length === 0 ? <p className="muted">This newsletter places no ads. In Compose &amp; schedule, pick an article feed and set its Marquee, Footer or Body slots; only positions the template has markers for are offered.</p>
        : slots.map((slot) => <div key={slot}>
          <label>{labelFor(slot) ? `${labelFor(slot)} — ${slot}` : slot}<textarea rows={4} value={fillFor(slot)} onChange={(e) => setFill(slot, e.target.value)} placeholder="Paste the LiveIntent HTML snippet" style={{ width: "100%", fontFamily: "monospace" }} /></label>
          {/* `requiredSlot` in newsletter.ts holds the edition for a named slot with no fill. */}
          {labelFor(slot) && !fillFor(slot).trim() && <p className="err" style={{ marginTop: 2 }}>Empty: this newsletter's sends are held until this slot is filled.</p>}
        </div>)}
      <p className="muted">A blank slot is saved as no fill at all. Ad HTML is stored verbatim — it is never tokenized, click-tracked or rewritten, which is why it is also never checked for you.</p>
      {orphans.length > 0 && <p className="muted">Stored fills for slots this template no longer declares: {orphans.map((slot) => <code key={slot} style={{ marginRight: 8 }}>{slot}</code>)} — saving now drops them.</p>}
      <div style={{ display: "flex", gap: 8 }}><button className="btn" onClick={() => void save()}>Save ad tags</button><button className="btn ghost" onClick={() => setEditing(false)}>Cancel</button></div>
    </div>}
    {series.loading && <SkeletonTable rows={3} label="Loading series…" />}
    {series.error && <p className="err">Could not load recurring series: {series.error}</p>}
    {!series.loading && !series.error && rows.length === 0 && !editing && <div className="card muted">No recurring series yet. Create one here, then bind its template’s declared slots.</div>}
    {rows.map((item) => <div className="card" key={item.seriesId}><div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><div><h2 style={{ marginTop: 0 }}>{item.name}</h2><p className="muted">{item.seriesId} · {item.templateId
        ? <>{item.cadence} · template <code>{item.templateId}</code></>
        // Scheduled in Compose: it has no saved template, and the cadence on its
        // row is a placeholder (the real timing is its cron, on Schedules).
        : "scheduled in Compose & schedule; timing is on Schedules"}</p></div><div style={{ display: "flex", gap: 8 }}><button className="btn ghost" onClick={() => setReportId(item.seriesId)}>Report</button><button className="btn ghost" onClick={() => begin(item)}>Edit</button></div></div><p>{item.adSlotFills.length ? item.adSlotFills.map((fill) => <code key={fill.slot} style={{ marginRight: 8 }}>{fill.slot}</code>) : <span className="muted">No filled slots</span>}</p></div>)}
  </div>;
}

/** The slots a series' Compose settings place, labelled by position. */
function placedSlots(policy?: NewsletterPolicy): { slot: string; label: string }[] {
  if (!policy) return [];
  return [
    ...(policy.marqueeSlot ? [{ slot: policy.marqueeSlot, label: "Marquee (top)" }] : []),
    ...(policy.bodySlots ?? []).map((slot, i) => ({ slot, label: `Body ad ${i + 1}` })),
    ...(policy.footerSlot ? [{ slot: policy.footerSlot, label: "Footer" }] : []),
  ];
}
